import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { IAPIState, IFormButtons, IJSONAPIErrorCodes, IStatus } from "shared/types";
import { addTemporaryToast } from "shared/components/Toast";

import { MemberForm } from "Bands/components";
import { BandSettingsSlugs, BAND_ACTIONS, IBandInvitationProps } from "Bands/types";
import { clearActionMeta, dataMetaSelector, editBandInvitation } from "Bands/state";
import { useBandRoute, useClearActionMetaOnUnMount } from "Bands/utils";
import { BAND_PATHS } from "Bands/router";
import {
  InvitationActionSourceType,
  useBandInvitationContext,
  usePermissionsContext,
} from "Bands/contexts";
import { API_ENDPOINTS } from "shared/constants";

const editInvitationCopy = copy.members.editInvitation;

export const EditBandInvitationForm: FC = () => {
  const { userCanManageBand } = usePermissionsContext();

  const dispatch = useDispatch();
  const { pushRoute } = useBandRoute();

  const { invitation, fetchSentInvitations, fetchPendingInvitations, invitationActionSource } =
    useBandInvitationContext();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.editBandInvitation));

  useClearActionMetaOnUnMount(BAND_ACTIONS.editBandInvitation);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      // refetech pending invitations
      invitationActionSource === InvitationActionSourceType.pending && fetchPendingInvitations();

      // refetech sent invitations
      invitationActionSource === InvitationActionSourceType.sent && fetchSentInvitations();

      dispatch(clearActionMeta(BAND_ACTIONS.editBandInvitation));
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editInvitationCopy.messages.success,
        })
      );

      pushRoute(BAND_PATHS.MEMBERS);
    }
  }, [
    apiMeta,
    pushRoute,
    dispatch,
    invitationActionSource,
    fetchSentInvitations,
    fetchPendingInvitations,
  ]);

  const buttons: IFormButtons = useMemo(
    () =>
      ({
        primary: true,
      } as IFormButtons),
    []
  );

  const defaultValues = useMemo(
    () => ({
      ...invitation,
      category: invitation?.category
        ? API_ENDPOINTS.EVENT_SETTING_ITEM(
            BandSettingsSlugs.band_member_categories,
            invitation?.category?.id
          )
        : undefined,
    }),
    [invitation]
  );

  const handleSubmit = (data: IBandInvitationProps) => {
    if (!invitation?.id) return;

    dispatch(
      editBandInvitation({
        id: invitation.id,
        data,
      })
    );
  };

  const error = useMemo(() => {
    const { errorCodes } = apiMeta?.error || {};

    if (errorCodes?.includes(IJSONAPIErrorCodes.EmailExistsException)) {
      return editInvitationCopy.messages.emailExists;
    }

    return !!apiMeta?.error;
  }, [apiMeta?.error]);

  return (
    <MemberForm
      error={error}
      apiStatus={apiMeta?.status}
      handleSubmit={handleSubmit}
      buttons={buttons}
      defaultValues={defaultValues}
      apiError={apiMeta?.error}
      canManageMemberCategories={userCanManageBand}
    />
  );
};
